import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Badge,
  Button,
  FormGroup,
  Input,
  Label,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Spinner,
} from "reactstrap";
import {
  Tenant,
  TenantSubscriptionStatus,
} from "../../../../services/tenantService";

interface TenantDetailOffcanvasProps {
  isOpen: boolean;
  loading: boolean;
  tenant: Tenant | null;
  notAvailable: string;
  formatDateTime: (value: string | null | undefined) => string;
  subscriptionStatusLabel: (status: TenantSubscriptionStatus) => string;
  subscriptionBadgeColor: (status: TenantSubscriptionStatus) => string;
  onClose: () => void;
  onImpersonate: (tenant: Tenant) => void;
  onBlock: (tenant: Tenant) => void;
  onUnblock: (tenant: Tenant) => void;
  onExtendCourtesy: (tenant: Tenant, days: number) => void;
  onToggleDemo: (tenant: Tenant, isDemo: boolean) => void;
  onClearCourtesy: (tenant: Tenant) => void;
  onMarkSubscriptionPaid: (tenant: Tenant) => void;
  onSyncSubscriptionPayment: (tenant: Tenant) => void;
  onToggleNurtureOptOut?: (tenant: Tenant, optOut: boolean) => void;
  nurtureOptOutBusy?: boolean;
  subscriptionBusy: boolean;
  canImpersonate: (tenant: Tenant) => boolean;
  impersonateUnavailableKey: (tenant: Tenant) => string | null;
  impersonatingUserId: number | null;
}

const DetailRow = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <div className="d-flex flex-wrap justify-content-between align-items-baseline gap-2 py-2 border-bottom border-light border-opacity-50">
    <span className="text-muted small">{label}</span>
    <span className="fw-medium text-end text-body">{value}</span>
  </div>
);

const INACTIVE_SUBSCRIPTION_STATUSES: TenantSubscriptionStatus[] = [
  "canceled",
  "past_due",
  "suspended",
];

const TenantDetailOffcanvas = ({
  isOpen,
  loading,
  tenant,
  notAvailable,
  formatDateTime,
  subscriptionStatusLabel,
  subscriptionBadgeColor,
  onClose,
  onImpersonate,
  onBlock,
  onUnblock,
  onExtendCourtesy,
  onToggleDemo,
  onClearCourtesy,
  onMarkSubscriptionPaid,
  onSyncSubscriptionPayment,
  onToggleNurtureOptOut,
  nurtureOptOutBusy = false,
  subscriptionBusy,
  canImpersonate,
  impersonateUnavailableKey,
  impersonatingUserId,
}: TenantDetailOffcanvasProps) => {
  const { t } = useTranslation();
  const blocked = tenant?.blocked_at != null;
  const subscription = tenant?.subscription ?? null;
  const unavailableKey = tenant ? impersonateUnavailableKey(tenant) : null;
  const isImpersonating =
    tenant?.owner_user != null &&
    impersonatingUserId === tenant.owner_user.id;
  const courtesyActive =
    tenant?.courtesy_until != null &&
    new Date(tenant.courtesy_until).getTime() > Date.now();
  const canMarkPaid =
    subscription != null &&
    INACTIVE_SUBSCRIPTION_STATUSES.includes(subscription.status);
  const showMarkPaidButton = subscription != null;
  const canSyncGateway =
    subscription != null && subscription.status !== "active";
  const gatewayMismatchHint =
    subscription != null &&
    canMarkPaid &&
    typeof subscription.external_subscription_id === "string" &&
    subscription.external_subscription_id.trim() !== "";

  return (
    <Offcanvas direction="end" isOpen={isOpen} toggle={onClose} style={{ width: 420 }}>
      <OffcanvasHeader toggle={onClose}>
        {tenant?.name ?? t("admin.tenants.detail.title")}
      </OffcanvasHeader>
      <OffcanvasBody>
        {loading || !tenant ? (
          <div className="d-flex justify-content-center py-5">
            <Spinner color="primary" />
          </div>
        ) : (
          <>
            <p className="text-muted mb-3">{t("admin.tenants.detail.subtitle")}</p>

            <h6 className="text-uppercase text-muted fs-12 mb-2">
              {t("admin.tenants.detail.sections.account")}
            </h6>
            <DetailRow label={t("admin.tenants.detail.fields.id")} value={tenant.id} />
            <DetailRow
              label={t("admin.tenants.table.document")}
              value={tenant.document || notAvailable}
            />
            <DetailRow
              label={t("admin.tenants.detail.fields.publicSlug")}
              value={tenant.public_slug || notAvailable}
            />
            <DetailRow
              label={t("admin.tenants.detail.fields.partner")}
              value={
                tenant.partner_id != null
                  ? `#${tenant.partner_id}`
                  : t("admin.tenants.detail.fields.directTenant")
              }
            />
            <DetailRow
              label={t("admin.tenants.table.createdAt")}
              value={formatDateTime(tenant.created_at)}
            />
            <DetailRow
              label={t("admin.tenants.table.lastLogin")}
              value={formatDateTime(tenant.last_login_at)}
            />
            <DetailRow
              label={t("admin.tenants.table.blockStatus")}
              value={
                <Badge color={blocked ? "danger" : "success"} className="fs-12">
                  {blocked
                    ? t("admin.tenants.blockStatus.blocked")
                    : t("admin.tenants.blockStatus.operational")}
                </Badge>
              }
            />
            {blocked && tenant.blocked_at ? (
              <DetailRow
                label={t("admin.tenants.detail.fields.blockedAt")}
                value={formatDateTime(tenant.blocked_at)}
              />
            ) : null}
            {blocked && tenant.block_audit?.super_admin_name ? (
              <DetailRow
                label={t("admin.tenants.detail.fields.blockedBy")}
                value={t("admin.tenants.detail.blockedByValue", {
                  name: tenant.block_audit.super_admin_name,
                  date: formatDateTime(
                    tenant.block_audit.blocked_at ?? tenant.blocked_at
                  ),
                })}
              />
            ) : null}
            {blocked && tenant.block_reason ? (
              <DetailRow
                label={t("admin.tenants.fields.blockReason.label")}
                value={tenant.block_reason}
              />
            ) : null}

            <h6 className="text-uppercase text-muted fs-12 mb-2 mt-4">
              {t("admin.tenants.detail.sections.owner")}
            </h6>
            <DetailRow
              label={t("admin.tenants.table.owner")}
              value={tenant.owner_user?.name ?? notAvailable}
            />
            <DetailRow
              label={t("admin.tenants.create.fields.email")}
              value={tenant.owner_user?.email ?? notAvailable}
            />
            <div className="d-grid gap-2 my-3">
              <Button
                tag={Link}
                to={`/super-admin/tenants/${tenant.id}/team`}
                color="soft-primary"
              >
                {t("admin.tenants.detail.actions.openTeam")}
              </Button>
              <Button
                tag={Link}
                to="/super-admin/accounts"
                color="soft-secondary"
              >
                {t("admin.tenants.detail.actions.openAccounts")}
              </Button>
            </div>

            <h6 className="text-uppercase text-muted fs-12 mb-2 mt-4">
              {t("admin.tenants.detail.sections.subscription")}
            </h6>
            <DetailRow
              label={t("admin.tenants.table.plan")}
              value={subscription?.plan?.name ?? notAvailable}
            />
            <DetailRow
              label={t("admin.tenants.table.subscriptionStatus")}
              value={
                subscription ? (
                  <span className="d-inline-flex flex-wrap align-items-center justify-content-end gap-1">
                    <Badge
                      color={subscriptionBadgeColor(subscription.status)}
                      className="fs-12"
                    >
                      {subscriptionStatusLabel(subscription.status)}
                    </Badge>
                    {gatewayMismatchHint ? (
                      <Badge
                        color="warning"
                        className="fs-12"
                        title={t(
                          "admin.tenants.subscription.gatewayMismatch.tooltip"
                        )}
                      >
                        {t("admin.tenants.subscription.gatewayMismatch.badge")}
                      </Badge>
                    ) : null}
                  </span>
                ) : (
                  notAvailable
                )
              }
            />
            <DetailRow
              label={t("admin.tenants.detail.fields.currency")}
              value={subscription?.currency ?? notAvailable}
            />
            <DetailRow
              label={t("admin.tenants.detail.fields.periodStart")}
              value={formatDateTime(subscription?.current_period_start)}
            />
            <DetailRow
              label={t("admin.tenants.detail.fields.periodEnd")}
              value={formatDateTime(subscription?.current_period_end)}
            />
            <DetailRow
              label={t("admin.tenants.table.counts")}
              value={t("admin.tenants.table.countsValue", {
                units: tenant.units_count,
                professionals: tenant.professionals_count,
              })}
            />
            {showMarkPaidButton ? (
              <>
                <p className="text-muted small mt-2 mb-2">
                  {t("admin.tenants.subscription.activate.help")}
                </p>
                {gatewayMismatchHint ? (
                  <p className="text-warning small mb-2">
                    {t("admin.tenants.subscription.gatewayMismatch.help")}
                  </p>
                ) : null}
                <div className="d-flex flex-wrap gap-2 mb-1">
                  <span
                    title={
                      canMarkPaid
                        ? undefined
                        : t("admin.tenants.subscription.activate.disabledActive")
                    }
                  >
                    <Button
                      color="success"
                      size="sm"
                      disabled={subscriptionBusy || !canMarkPaid}
                      onClick={() => onMarkSubscriptionPaid(tenant)}
                    >
                      <i className="ri-checkbox-circle-line align-bottom me-1" />
                      {t("admin.tenants.subscription.activate.action")}
                    </Button>
                  </span>
                  {canSyncGateway ? (
                    <Button
                      color="soft-info"
                      size="sm"
                      disabled={subscriptionBusy}
                      onClick={() => onSyncSubscriptionPayment(tenant)}
                    >
                      <i className="ri-refresh-line align-bottom me-1" />
                      {t("admin.tenants.subscription.sync.action")}
                    </Button>
                  ) : null}
                </div>
              </>
            ) : null}

            <h6 className="text-uppercase text-muted fs-12 mb-2 mt-4">
              {t("admin.tenants.detail.sections.courtesy")}
            </h6>
            <DetailRow
              label={t("admin.tenants.courtesy.demo")}
              value={
                <Badge
                  color={tenant.is_demo ? "info" : "secondary"}
                  className="fs-12"
                >
                  {tenant.is_demo
                    ? t("admin.tenants.courtesy.demoOn")
                    : t("admin.tenants.courtesy.demoOff")}
                </Badge>
              }
            />
            <DetailRow
              label={t("admin.tenants.courtesy.until")}
              value={
                courtesyActive ? (
                  <span>
                    {formatDateTime(tenant.courtesy_until)}{" "}
                    <Badge color="warning" className="fs-12 ms-1">
                      {t("admin.tenants.courtesy.active")}
                    </Badge>
                  </span>
                ) : (
                  notAvailable
                )
              }
            />
            <p className="text-muted small mt-2 mb-2">
              {t("admin.tenants.courtesy.help")}
            </p>
            <div className="d-flex flex-wrap gap-2 mb-2">
              <Button
                color="soft-warning"
                size="sm"
                disabled={subscriptionBusy || !subscription}
                onClick={() => onExtendCourtesy(tenant, 7)}
              >
                {t("admin.tenants.courtesy.extend7")}
              </Button>
              <Button
                color="soft-warning"
                size="sm"
                disabled={subscriptionBusy || !subscription}
                onClick={() => onExtendCourtesy(tenant, 30)}
              >
                {t("admin.tenants.courtesy.extend30")}
              </Button>
              <Button
                color="soft-warning"
                size="sm"
                disabled={subscriptionBusy || !subscription}
                onClick={() => onExtendCourtesy(tenant, 365)}
              >
                {t("admin.tenants.courtesy.extend365")}
              </Button>
              {courtesyActive ? (
                <Button
                  color="light"
                  size="sm"
                  disabled={subscriptionBusy}
                  onClick={() => onClearCourtesy(tenant)}
                >
                  {t("admin.tenants.courtesy.clear")}
                </Button>
              ) : null}
            </div>
            <FormGroup switch className="mb-0">
              <Input
                type="switch"
                role="switch"
                id={`tenant-demo-${tenant.id}`}
                checked={Boolean(tenant.is_demo)}
                disabled={subscriptionBusy}
                onChange={(event) =>
                  onToggleDemo(tenant, event.target.checked)
                }
              />
              <Label check htmlFor={`tenant-demo-${tenant.id}`}>
                {t("admin.tenants.courtesy.demoToggle")}
              </Label>
            </FormGroup>

            <h6 className="text-uppercase text-muted fs-12 mb-2 mt-4">
              {t("admin.tenants.detail.sections.settings")}
            </h6>
            <DetailRow
              label={t("admin.tenants.detail.fields.locale")}
              value={tenant.settings_summary?.locale ?? notAvailable}
            />
            <DetailRow
              label={t("admin.tenants.detail.fields.timezone")}
              value={tenant.settings_summary?.timezone ?? notAvailable}
            />
            <DetailRow
              label={t("admin.tenants.detail.fields.currency")}
              value={tenant.settings_summary?.currency_code ?? notAvailable}
            />

            <h6 className="text-uppercase text-muted fs-12 mb-2 mt-4">
              {t("admin.tenants.detail.sections.activation")}
            </h6>
            <DetailRow
              label={t("admin.tenants.detail.fields.trialStarted")}
              value={formatDateTime(tenant.activation_summary?.trial_started_at)}
            />
            <DetailRow
              label={t("admin.tenants.detail.fields.trialEnds")}
              value={formatDateTime(tenant.activation_summary?.trial_ends_at)}
            />
            <DetailRow
              label={t("admin.tenants.detail.fields.aha")}
              value={formatDateTime(tenant.activation_summary?.aha_completed_at)}
            />
            <DetailRow
              label={t("admin.tenants.detail.fields.concierge")}
              value={formatDateTime(
                tenant.activation_summary?.concierge_completed_at
              )}
            />
            <DetailRow
              label={t("admin.tenants.detail.fields.nurtureTips")}
              value={
                tenant.activation_summary?.nurture_opt_out ? (
                  <Badge color="warning" className="fs-12">
                    {t("admin.tenants.detail.nurtureOptOut.badge")}
                  </Badge>
                ) : (
                  <Badge color="success" className="fs-12">
                    {t("admin.tenants.detail.nurtureOptOut.active")}
                  </Badge>
                )
              }
            />
            {onToggleNurtureOptOut ? (
              <FormGroup switch className="mt-2 mb-0">
                <Input
                  type="switch"
                  role="switch"
                  id={`tenant-nurture-opt-out-${tenant.id}`}
                  checked={Boolean(tenant.activation_summary?.nurture_opt_out)}
                  disabled={nurtureOptOutBusy}
                  onChange={(event) =>
                    onToggleNurtureOptOut(tenant, event.target.checked)
                  }
                />
                <Label check htmlFor={`tenant-nurture-opt-out-${tenant.id}`}>
                  {t("admin.tenants.detail.nurtureOptOut.toggle")}
                </Label>
              </FormGroup>
            ) : null}

            <div className="d-flex flex-wrap gap-2 mt-4">
              <Button
                color="soft-primary"
                size="sm"
                title={unavailableKey ? t(unavailableKey) : undefined}
                disabled={!canImpersonate(tenant) || isImpersonating}
                onClick={() => onImpersonate(tenant)}
              >
                {isImpersonating ? (
                  <Spinner size="sm" className="me-1" />
                ) : (
                  <i className="ri-shield-user-line align-bottom me-1" />
                )}
                {t("admin.tenants.impersonate.action")}
              </Button>
              {blocked ? (
                <Button color="soft-success" size="sm" onClick={() => onUnblock(tenant)}>
                  <i className="ri-lock-unlock-line align-bottom me-1" />
                  {t("admin.tenants.actions.unblock")}
                </Button>
              ) : (
                <Button color="soft-danger" size="sm" onClick={() => onBlock(tenant)}>
                  <i className="ri-forbid-line align-bottom me-1" />
                  {t("admin.tenants.actions.block")}
                </Button>
              )}
              <Button
                tag={Link}
                to={`/super-admin/activity-logs?tenant_id=${tenant.id}`}
                color="soft-secondary"
                size="sm"
              >
                <i className="ri-history-line align-bottom me-1" />
                {t("admin.tenants.actions.viewAudit")}
              </Button>
            </div>
          </>
        )}
      </OffcanvasBody>
    </Offcanvas>
  );
};

export default TenantDetailOffcanvas;
